import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../services/api';
import Spinner from './Spinner';

const ResetPassword: React.FC = () => {
    const [email, setEmail] = useState('');
    const [confirmationCode, setConfirmationCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        // Get email from session storage
        const storedEmail = sessionStorage.getItem('resetEmail');
        if (storedEmail) {
            setEmail(storedEmail);
        } else {
            // If no email in session, redirect to forgot password
            navigate('/forgot-password');
        }
    }, [navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Validate passwords match
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        // Basic password validation
        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }

        // Validate confirmation code
        if (!/^\d{6}$/.test(confirmationCode)) {
            setError('Confirmation code must be 6 digits');
            return;
        }

        setLoading(true);

        try {
            const response = await authApi.resetPassword({
                email,
                confirmationCode,
                newPassword,
            });

            if (response.status === 'success') {
                setSuccess('Password has been successfully reset! Redirecting to login...');
                // Clear the reset email from session
                sessionStorage.removeItem('resetEmail');

                // Redirect to login after a short delay
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            } else {
                setError(response.message || 'Failed to reset password');
            }
        } catch (err) {
            setError('An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleBackToForgotPassword = () => {
        navigate('/forgot-password');
    };

    const handleBackToLogin = () => {
        // Clear session data and go back to login
        sessionStorage.removeItem('resetEmail');
        navigate('/login');
    };

    if (!email) {
        return null; // Will redirect in useEffect
    }

    return (
        <div
            className='min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8'
            style={{ backgroundColor: '#1D1A31' }}
        >
            <div className='max-w-md w-full space-y-8'>
                {/* Header */}
                <div className='text-center'>
                    <div className='mx-auto flex items-center justify-center h-16 w-16 rounded-full border-2 border-orange-400 mb-4'>
                        <svg
                            className='h-8 w-8 text-orange-500'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                        >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth='2'
                                d='M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z'
                            ></path>
                        </svg>
                    </div>
                    <h1 className='text-4xl font-bold text-orange-500 mb-2'>Enter Reset Code</h1>
                    <p className='text-lg text-gray-400'>
                        We've sent a 6-digit code to{' '}
                        <span className='font-semibold text-orange-500'>{email}</span>
                    </p>
                    <p className='text-sm text-gray-500 mt-2'>
                        Enter the code and your new password below.
                    </p>
                </div>

                {/* Reset Form */}
                <div className='bg-white py-8 px-6 shadow-xl rounded-2xl border border-gray-100'>
                    <form onSubmit={handleSubmit} className='space-y-6'>
                        {/* Confirmation Code Field */}
                        <div>
                            <label
                                htmlFor='confirmationCode'
                                className='block text-sm font-medium text-gray-700 mb-2'
                            >
                                Confirmation Code
                            </label>
                            <input
                                type='text'
                                id='confirmationCode'
                                value={confirmationCode}
                                onChange={e =>
                                    setConfirmationCode(
                                        e.target.value.replace(/\D/g, '').slice(0, 6)
                                    )
                                }
                                required
                                disabled={loading}
                                placeholder='Enter 6-digit code'
                                maxLength={6}
                                className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed font-mono tracking-wider text-center text-lg'
                            />
                        </div>

                        {/* New Password Field */}
                        <div>
                            <label
                                htmlFor='newPassword'
                                className='block text-sm font-medium text-gray-700 mb-2'
                            >
                                New Password
                            </label>
                            <input
                                type='password'
                                id='newPassword'
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                required
                                disabled={loading}
                                placeholder='Enter new password (min 8 characters)'
                                className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed'
                            />
                        </div>

                        {/* Confirm Password Field */}
                        <div>
                            <label
                                htmlFor='confirmPassword'
                                className='block text-sm font-medium text-gray-700 mb-2'
                            >
                                Confirm New Password
                            </label>
                            <input
                                type='password'
                                id='confirmPassword'
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                required
                                disabled={loading}
                                placeholder='Confirm new password'
                                className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed'
                            />
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm'>
                                {error}
                            </div>
                        )}

                        {/* Success Message */}
                        {success && (
                            <div className='bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm'>
                                {success}
                            </div>
                        )}

                        {/* Submit Button */}
                        <button
                            type='submit'
                            disabled={loading}
                            className='w-full bg-gradient-to-r from-yellow-400 to-orange-500 !text-black font-semibold py-3 px-4 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none'
                        >
                            {loading ? (
                                <div className='flex items-center justify-center'>
                                    <Spinner size='md' color='black' className='-ml-1 mr-3' />
                                    Resetting Password...
                                </div>
                            ) : (
                                'Reset Password'
                            )}
                        </button>
                    </form>

                    {/* Action Links */}
                    <div className='mt-6 flex flex-col sm:flex-row gap-3 justify-center'>
                        <button
                            type='button'
                            onClick={handleBackToForgotPassword}
                            disabled={loading}
                            className='text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                            Resend Code
                        </button>
                        <span className='hidden sm:inline text-gray-300'>•</span>
                        <button
                            type='button'
                            onClick={handleBackToLogin}
                            disabled={loading}
                            className='text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                            Back to Login
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
